import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  FaArrowLeft, FaSync, FaPrint, FaShieldAlt,
  FaCheckCircle, FaTimesCircle, FaClock, FaBan,
  FaBuilding, FaIdCard, FaCalendarAlt, FaPhoneAlt,
  FaQrcode, FaExclamationTriangle, FaHardHat
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getToken } from '../../../utils/auth';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import { formatDate } from '../../portals/Contractor/workspace/utils/dates';
import { qualsFromRecord, qualExpiryState, isSiteCard } from '../../portals/Contractor/workspace/utils/qualifications';
import './DigitalBadge.css';

const initialsOf = (name) => String(name || '')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

/* Apple Wallet badge SVG — official "Add to Apple Wallet" button */
const AddToWalletButton = ({ onClick, disabled }) => (
  <button
    className="db-wallet-btn"
    onClick={onClick}
    disabled={disabled}
    aria-label="Add to Apple Wallet"
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 40" width="160" height="54">
      <rect rx="5" ry="5" width="120" height="40" fill="#000" />
      <text x="60" y="15" textAnchor="middle" fill="#fff" fontSize="7" fontFamily="-apple-system,SF Pro Text,Helvetica,sans-serif" fontWeight="400">Add to</text>
      <text x="60" y="28" textAnchor="middle" fill="#fff" fontSize="10.5" fontFamily="-apple-system,SF Pro Display,Helvetica,sans-serif" fontWeight="600"> Apple Wallet</text>
    </svg>
  </button>
);

const STATUS_CONFIG = {
  active: { icon: FaCheckCircle, label: 'Active', className: 'db-status-active' },
  suspended: { icon: FaClock, label: 'Suspended', className: 'db-status-suspended' },
  revoked: { icon: FaBan, label: 'Revoked', className: 'db-status-revoked' },
  expired: { icon: FaTimesCircle, label: 'Expired', className: 'db-status-expired' },
};

const ROLE_COLORS = {
  contractor: { bg: '#1A80E5', accent: '#60a5fa' },
  staff: { bg: '#0f172a', accent: '#94a3b8' },
  building_manager: { bg: '#056046', accent: '#6ee7b7' },
  cloud_staff: { bg: '#0f172a', accent: '#93c5fd' },
  fm: { bg: '#581c87', accent: '#c084fc' },
  admin: { bg: '#0f172a', accent: '#fdba74' },
  super_admin: { bg: '#0f172a', accent: '#fdba74' },
};

/**
 * Digital ID badge. `embedded` = rendered inside the contractor workspace
 * shell, which already provides the page header and navigation.
 */
export default function DigitalBadge({ onBack, embedded = false }) {
  const navigate = useNavigate();
  const [badge, setBadge] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [qrDataURL, setQrDataURL] = useState('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingRegen, setConfirmingRegen] = useState(false);
  // Site details from the contractor staff record (primary card, emergency contact).
  const [site, setSite] = useState(null);

  const headers = useCallback(() => {
    const t = getToken();
    return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  }, []);

  const fetchBadge = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(getApiEndpoint('/api/badges/me'), { headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load badge');
      }
      const data = await res.json();
      setBadge(data.badge);
      setWallet(data.wallet);
    } catch (err) {
      setError(err.message || 'Failed to load badge');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchBadge(); }, [fetchBadge]);

  // Contractor staff: pull their primary site card and emergency contact (optional extras).
  useEffect(() => {
    if (localStorage.getItem('userRole') !== 'contractor') return undefined;
    let alive = true;
    fetch(getApiEndpoint('/api/contractor/staff/me'), { headers: headers() })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => {
        if (!alive || !me) return;
        const info = me.info || {};
        const quals = qualsFromRecord(me.tags || [], info);
        const card = quals.find((q) => isSiteCard(q.name)) || null;
        setSite({
          card,
          emergency: info.emergencyName && info.emergencyPhone
            ? { name: info.emergencyName, phone: info.emergencyPhone, relationship: info.emergencyRelationship || '' }
            : null,
        });
      })
      .catch(() => { /* extras only */ });
    return () => { alive = false; };
  }, [headers]);

  // Generate QR code when badge loads
  useEffect(() => {
    if (!wallet?.verifyUrl) return;
    QRCode.toDataURL(wallet.verifyUrl, {
      width: 280,
      margin: 3,
      errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#ffffff' },
    }).then(setQrDataURL).catch(() => setQrDataURL(''));
  }, [wallet]);

  const handleRegenerate = async () => {
    setConfirmingRegen(false);
    setRegenerating(true);
    try {
      const res = await fetch(getApiEndpoint('/api/badges/regenerate'), {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) throw new Error('Failed to regenerate');
      const data = await res.json();
      setBadge(data.badge);
      setWallet(data.wallet);
      toast.success('New badge issued. The old QR code no longer verifies.');
    } catch {
      toast.error('Could not issue a new badge. Your current badge is unchanged.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleAddToWallet = async () => {
    setWalletLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/badges/wallet-pass'), { headers: headers() });
      if (res.status === 503) {
        toast.error('Apple Wallet passes aren’t available right now. Your badge still works from this page.');
        return;
      }
      if (!res.ok) throw new Error('Failed to generate pass');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cloudsafe-badge-${badge.badgeNumber}.pkpass`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Wallet pass downloaded');
    } catch {
      toast.error('Could not generate wallet pass');
    } finally {
      setWalletLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleBack = () => {
    if (onBack) return onBack();
    // Route back to the correct dashboard based on role
    const role = localStorage.getItem('userRole');
    if (role === 'contractor') return navigate('/contractor/workspace');
    if (role === 'staff') return navigate('/staff/dashboard');
    if (role === 'building_manager') return navigate('/building-manager/dashboard');
    if (role === 'cloud_staff' || role === 'super_admin') return navigate('/cloud-staff/dashboard');
    if (role === 'fm') return navigate('/fm/dashboard');
    navigate(-1);
  };

  const pageClass = `db-page${embedded ? ' db-embedded' : ''}`;

  if (loading) {
    return (
      <div className={pageClass} role="status">
        <div className="db-loading">
          <div className="db-loading-spinner" />
          <p>Loading your digital badge…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageClass}>
        <div className="db-error" role="alert">
          <FaExclamationTriangle aria-hidden="true" />
          <p><strong>Couldn't load your badge.</strong> {error}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button type="button" className="db-btn db-btn-outline" onClick={fetchBadge}>Try again</button>
            {!embedded && <button type="button" className="db-btn db-btn-outline" onClick={handleBack}>Go back</button>}
          </div>
        </div>
      </div>
    );
  }

  if (!badge) return null;

  const roleColor = ROLE_COLORS[badge.role] || ROLE_COLORS.staff;
  const statusCfg = STATUS_CONFIG[badge.status] || STATUS_CONFIG.active;
  const StatusIcon = statusCfg.icon;
  const isActive = badge.status === 'active';

  const actions = (
    <div className={embedded ? 'db-toolbar-actions' : 'db-header-actions'}>
      <button type="button" className={embedded ? 'db-btn db-btn-outline' : 'db-action-btn'} onClick={handlePrint}>
        <FaPrint aria-hidden="true" /> Print
      </button>
      <button
        type="button"
        className={embedded ? 'db-btn db-btn-outline' : 'db-action-btn'}
        onClick={() => setConfirmingRegen(true)}
        disabled={regenerating || confirmingRegen}
      >
        <FaSync aria-hidden="true" className={regenerating ? 'db-spin' : ''} /> {regenerating ? 'Issuing…' : 'Replace badge'}
      </button>
    </div>
  );

  return (
    <div className={pageClass}>
      {embedded ? (
        <div className="db-toolbar">
          <h2 className="db-toolbar-title">ID badge</h2>
          {actions}
        </div>
      ) : (
        <header className="db-header">
          <div className="db-header-inner">
            <button type="button" className="db-back-btn" onClick={handleBack}>
              <FaArrowLeft aria-hidden="true" /> Back
            </button>
            <h1 className="db-header-title">Digital ID badge</h1>
            {actions}
          </div>
        </header>
      )}

      <div className="db-content">
        {confirmingRegen && (
          <div className="db-confirm" role="alertdialog" aria-labelledby="db-confirm-title" aria-describedby="db-confirm-body">
            <h3 id="db-confirm-title">Replace your badge?</h3>
            <p id="db-confirm-body">
              Your current badge is revoked straight away and its QR code stops verifying, including any printed copy
              or Wallet pass. A new badge is issued in its place. Do this if your badge was lost or copied.
            </p>
            <div className="db-confirm-actions">
              <button type="button" className="db-btn db-btn-outline" onClick={() => setConfirmingRegen(false)} autoFocus>Keep current badge</button>
              <button type="button" className="db-btn db-btn-danger" onClick={handleRegenerate}>Revoke and replace</button>
            </div>
          </div>
        )}

        {/* The Badge Card */}
        <div className="db-card" style={{ '--badge-bg': roleColor.bg, '--badge-accent': roleColor.accent }}>
          {/* Card Header */}
          <div className="db-card-header">
            <img src={cloudsafeLogo} alt="CloudSafe" className="db-card-logo" />
            <div className="db-card-header-text">
              <span className="db-card-org">CloudSafe</span>
              <span className="db-card-subtitle">Digital ID Badge</span>
            </div>
            <div className={`db-card-status ${statusCfg.className}`}>
              <StatusIcon aria-hidden="true" />
              <span>{statusCfg.label}</span>
            </div>
          </div>

          {/* Card Body */}
          <div className="db-card-body">
            {/* Photo + Name */}
            <div className="db-card-identity">
              <div className="db-card-avatar" role="img" aria-label={`Initials of ${badge.displayName}`}>
                <span className="db-card-initials">{initialsOf(badge.displayName)}</span>
              </div>
              <div className="db-card-name-block">
                <h2 className="db-card-name">{badge.displayName}</h2>
                <span className="db-card-role" style={{ backgroundColor: roleColor.accent + '33', color: roleColor.accent }}>
                  {badge.roleDisplay}
                </span>
              </div>
            </div>

            {/* Details Grid */}
            <div className="db-card-details">
              <div className="db-detail">
                <FaIdCard className="db-detail-icon" aria-hidden="true" />
                <div>
                  <span className="db-detail-label">Badge ID</span>
                  <span className="db-detail-value">{badge.badgeNumber}</span>
                </div>
              </div>
              {badge.userCode && (
                <div className="db-detail">
                  <FaShieldAlt className="db-detail-icon" aria-hidden="true" />
                  <div>
                    <span className="db-detail-label">CloudSafe ID</span>
                    <span className="db-detail-value">{badge.userCode}</span>
                  </div>
                </div>
              )}
              {badge.companyName && (
                <div className="db-detail">
                  <FaBuilding className="db-detail-icon" aria-hidden="true" />
                  <div>
                    <span className="db-detail-label">Company</span>
                    <span className="db-detail-value">{badge.companyName}</span>
                  </div>
                </div>
              )}
              <div className="db-detail">
                <FaCalendarAlt className="db-detail-icon" aria-hidden="true" />
                <div>
                  <span className="db-detail-label">Issued</span>
                  <span className="db-detail-value">{formatDate(badge.issuedAt)}</span>
                </div>
              </div>
              {site?.card && (
                <div className="db-detail db-detail-wide">
                  <FaHardHat className="db-detail-icon" aria-hidden="true" />
                  <div>
                    <span className="db-detail-label">Site card</span>
                    <span className="db-detail-value">{site.card.name}{site.card.cardNumber ? ` · ${site.card.cardNumber}` : ''}</span>
                    <span className="db-detail-sub">{qualExpiryState(site.card.expiry).label}</span>
                  </div>
                </div>
              )}
              {site?.emergency && (
                <div className="db-detail db-detail-wide">
                  <FaPhoneAlt className="db-detail-icon" aria-hidden="true" />
                  <div>
                    <span className="db-detail-label">Emergency contact</span>
                    <span className="db-detail-value">
                      {site.emergency.name}{site.emergency.relationship ? ` (${site.emergency.relationship})` : ''} · {site.emergency.phone}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* QR Code */}
            <div className="db-card-qr">
              {qrDataURL ? (
                <img src={qrDataURL} alt="QR code that verifies this badge" className="db-qr-image" />
              ) : (
                <div className="db-qr-placeholder">
                  <FaQrcode aria-hidden="true" />
                  <span>Generating…</span>
                </div>
              )}
              <span className="db-qr-hint">Scan to verify this badge</span>
            </div>
          </div>

          {/* Card Footer */}
          <div className="db-card-footer">
            <span className="db-card-serial">{badge.badgeSerial?.split('-').slice(0, 3).join('-')}…</span>
            <span className="db-card-version">v{badge.version}</span>
          </div>
        </div>

        {/* Actions below the card */}
        {wallet?.walletAvailable && (
          <div className="db-actions">
            <AddToWalletButton onClick={handleAddToWallet} disabled={walletLoading || !isActive} />
          </div>
        )}

        {/* Security info */}
        <div className="db-security">
          <FaShieldAlt className="db-security-icon" aria-hidden="true" />
          <div>
            <h3>About this badge</h3>
            <p>Your badge is linked to your CloudSafe account. Site staff scan the QR code to check it's genuine and current. The code opens a verification page and doesn't contain your personal details.</p>
            <p>If your badge is lost or copied, choose <strong>Replace badge</strong> to revoke it and issue a new one.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
